import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import i18n from "@config/i18n";
import { useApolloClient } from "@apollo/client";
import { packageSection, packageSectionWithLinks } from "../graphql/packageSection.query";
import { ISectionData } from "@modules/sections/ISections";
import { serializeSections } from "@services/collections/sections/sectionsAdapters";
import { CubeQuery, Label, Package, PackageInfo } from "@models/ISchema";
import { IInfoHeader } from "@common/components/Header/IHeaderModule";
import { Tooltip } from "@foris/avocado-suite";
import { Link } from "react-router-dom";

interface UseGetPackageSectionParams {
  userId?: string;
  resourceId: string;
  scenarioId: string;
  originId: string;
  workspaceId: string;
  fetchWithLinks?: boolean;
  includeAnnualData?: boolean;
}

type HeaderData = Pick<IInfoHeader, "moreDetails" | "title" | "topTitle"> & {
  labels: Label[];
  systemLabels: Label[];
};

const getHeaderInfo = (info: Package, contextUrl: string): HeaderData => {
  const vacanciesSource = {
    GROUP: i18n.t("dimension.package.header.vacancies-source.group"),
    PACKAGE: i18n.t("dimension.package.header.vacancies-source.package"),
  };

  const details = [
    {
      title: i18n.t("dimension.package.header.more-details.vacancies"),
      content: (
        <Tooltip label={vacanciesSource?.[info?.vacanciesSource] || ""} placement="topLeft">
          {info?.vacancies?.toString() || ""}
        </Tooltip>
      ),
    },
  ];

  if (!!info?.isReplicated && info?.sourcePackage?.code) {
    details.push({
      title: i18n.t("dimension.package.header.replicated-from"),
      content: (
        <Link to={`/scheduler/editor/package/${contextUrl}/${info?.sourcePackage?.id}`}>
          {info?.sourcePackage?.code}
        </Link>
      ),
    });
  }

  const labels = [];
  const systemLabels = [];

  info?.labels?.forEach(label => {
    if (label?.category?.labelSource?.code !== "USER-DEFINED") {
      systemLabels.push(label);
    } else {
      labels.push(label);
    }
  });

  const getTitle = (): string => {
    if (info?.name) {
      return info.name;
    }

    if (info?.population) {
      return `${info?.population?.program?.code}, ${info?.population?.program?.name}`;
    }

    return "";
  };

  return {
    title: getTitle(),
    topTitle: info?.code ? `${i18n.t("dimension.package.header.code")}: ${info?.code}` : "",
    moreDetails: details,
    systemLabels,
    labels,
  };
};

/** Loads the package projection shared by the league summary, calendar, and annual schedule. */
const useGetPackageSection = ({
  userId,
  resourceId,
  scenarioId,
  originId,
  workspaceId,
  fetchWithLinks = false,
  includeAnnualData = false,
}: UseGetPackageSectionParams) => {
  const client = useApolloClient();
  const scopeKey = JSON.stringify([userId, workspaceId, scenarioId, originId, resourceId]);
  const requestId = useRef(0);

  const [packageData, setPackageData] = useState<Package | null>(null);
  const [annualCube, setAnnualCube] = useState<CubeQuery | null>(null);
  const [loadedScope, setLoadedScope] = useState<string | null>(null);
  const [ogHeaderData, setOgHeaderData] = useState<Package | null>(null);
  const [sections, setSections] = useState<ISectionData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorScope, setErrorScope] = useState<string | null>(null);

  useEffect(
    () => () => {
      requestId.current += 1;
    },
    [scopeKey, includeAnnualData],
  );

  /** Reloads the package with annual fields only after the annual tab has been opened. */
  const loadPackageData = useCallback(async () => {
    const currentRequest = ++requestId.current;
    const requestAnnualData = fetchWithLinks && includeAnnualData;

    try {
      setIsLoading(true);
      setErrorScope(null);

      const { data } = await client.query({
        query: fetchWithLinks ? packageSectionWithLinks : packageSection,
        variables: {
          resourceId,
          scenarioId,
          originId,
          resourceType: "PACKAGE",
          ...(fetchWithLinks && { includeAnnualData: requestAnnualData }),
        },
        fetchPolicy: "network-only",
      });

      if (currentRequest !== requestId.current) return;

      if (data?.cube) {
        const cubeQuery: CubeQuery = data.cube;
        const info = cubeQuery.editorView?.info as PackageInfo | undefined;

        if (info && info.__typename === "PackageInfo") {
          const sections: ISectionData = serializeSections(data);
          setOgHeaderData(info.package);
          setSections(sections);
          setPackageData(info?.package);
          setAnnualCube(requestAnnualData ? cubeQuery : null);
          setLoadedScope(scopeKey);
        } else {
          setErrorScope(scopeKey);
        }
      } else {
        setErrorScope(scopeKey);
      }
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      console.error(error);
      setErrorScope(scopeKey);
    } finally {
      if (currentRequest === requestId.current) setIsLoading(false);
    }
  }, [client, fetchWithLinks, includeAnnualData, originId, resourceId, scenarioId, scopeKey]);

  const headerData = useMemo(() => {
    if (loadedScope === scopeKey && ogHeaderData) {
      return getHeaderInfo(ogHeaderData, `${workspaceId}/${scenarioId}/${originId}`) as HeaderData;
    }
    return {} as HeaderData;
  }, [loadedScope, scopeKey, ogHeaderData, workspaceId, scenarioId, originId, i18n.language]);

  const hasError = errorScope === scopeKey;
  const hasCurrentData = loadedScope === scopeKey;

  return {
    isLoading: isLoading || (!hasCurrentData && !hasError),
    hasError,
    sections: hasCurrentData ? sections : null,
    setSections,
    headerData,
    loadPackageData,
    packageData: hasCurrentData ? packageData : null,
    annualCube: hasCurrentData ? annualCube : null,
  };
};

export default useGetPackageSection;
